import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  HiOutlineExclamationTriangle,
  HiOutlineChartBar,
  HiOutlineBuildingOffice2,
  HiOutlineArrowLeft,
  HiOutlineShieldCheck,
  HiOutlineCalendar,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
  HiOutlineClipboardDocumentList,
  HiOutlineClock,
  HiOutlineCheckCircle,
  HiOutlineDocumentArrowDown,
  HiOutlineUserGroup,
} from "react-icons/hi2";
import { authService, plantService, masterDataService } from "../services";
import hazardReportService from "../services/hazardReportService";
import hazardCategoryService from "../services/hazardCategoryService";
import { usePermissions } from "../hooks/usePermissions";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import MultiSelectDropdown from "../components/MultiSelectDropdown";
import { SEMANTIC_SUCCESS_MID } from '../theme/chartColors';
import {
  showReportGeneratingToast,
  reportGenerationDone,
  reportGenerationFailed,
} from '../utils/reportGenerationToast';
import "./Reports.css";

const HazardReports = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();

  const canView = hasPermission("Reports.View");

  const [activeTab, setActiveTab] = useState("category");
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [plants, setPlants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [severityLevels, setSeverityLevels] = useState([]);
  
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
    plantIds: [],
    categoryId: "",
    severityLevelId: "",
  });

  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (tenantId && canView) {
      loadInitialData();
    }
  }, [tenantId, canView]);

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location]);

  const loadInitialData = async () => {
    try {
      const [plantsData, categoriesData, severityData] = await Promise.all([
        plantService.getAllPlants(),
        hazardCategoryService.getCategories(),
        masterDataService.getSeverityLevels(tenantId)
      ]);
      setPlants(plantsData || []);
      
      const uniqueCategories = [];
      const seenCategoryNames = new Set();
      (categoriesData || []).forEach(cat => {
        const name = cat.categoryName || cat.name;
        if (name && !seenCategoryNames.has(name.toLowerCase())) {
          seenCategoryNames.add(name.toLowerCase());
          uniqueCategories.push(cat);
        }
      });
      setCategories(uniqueCategories);

      setSeverityLevels(severityData || []);

      // Auto-select plant ONLY for single-plant users
      if (plantsData?.length === 1) {
        setFilters(prev => ({ ...prev, plantIds: [plantsData[0].plantId] }));
      }
    } catch (err) {
      console.error("Error loading initial data:", err);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handlePlantChange = (vals) => {
    setFilters(prev => ({ ...prev, plantIds: vals }));
  };

  const handleGenerateReport = async () => {
    if (!canView) return;
    if (!filters.startDate || !filters.endDate) {
      setError("Please select both Start Date and End Date.");
      return;
    }
    setError("");

    showReportGeneratingToast();
    try {
      setLoading(true);
      const filterParams = {
        startDate: filters.startDate,
        endDate: filters.endDate,
      };
      if (filters.plantIds && filters.plantIds.length > 0) filterParams.plantIds = filters.plantIds;
      if (filters.categoryId) filterParams.categoryId = parseInt(filters.categoryId, 10);
      if (filters.severityLevelId) filterParams.severityLevelId = parseInt(filters.severityLevelId, 10);

      const data = await hazardReportService.getStatistics(filterParams);
      setReportData(data);
      reportGenerationDone();
    } catch (err) {
      console.error("Error generating report:", err);
      reportGenerationFailed("Could not generate hazard report.");
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!reportData) return;

    let exportData = [];
    let fileName = `Hazard_Report_${activeTab}_${filters.startDate}_to_${filters.endDate}`;

    if (activeTab === "category") {
      exportData = (reportData.categoryDistribution || []).map((item) => ({
        Category: item.categoryName,
        Count: item.count,
      }));
    } else if (activeTab === "severity") {
      exportData = (reportData.severityDistribution || []).map((item) => ({
        "Severity Level": item.severityLevelName,
        Count: item.count,
      }));
    } else if (activeTab === "action") {
      exportData = [
        { Metric: "Actions Taken", Value: reportData.immediateActionStats?.taken || 0 },
        { Metric: "Pending Actions", Value: reportData.immediateActionStats?.pending || 0 },
      ];
    } else if (activeTab === "department") {
      exportData = (reportData.departmentDistribution || []).map((item) => ({
        Department: item.departmentName,
        Count: item.count,
      }));
    } else if (activeTab === "reporter") {
      exportData = (reportData.reporterDistribution || []).map((item) => ({
        Reporter: item.reporterName,
        Frequency: item.count,
      }));
    }

    if (exportData.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    saveAs(dataBlob, `${fileName}.xlsx`);
  };

  const tabs = [
    { id: "category", label: "Category Wise", icon: HiOutlineChartBar },
    { id: "severity", label: "Severity Estimate", icon: HiOutlineExclamationTriangle },
    { id: "action", label: "Immediate Action", icon: HiOutlineShieldCheck },
    { id: "department", label: "Department Wise", icon: HiOutlineBuildingOffice2 },
    { id: "reporter", label: "Reporter Performance", icon: HiOutlineUserGroup },
  ];

  const ReportBarChart = ({ data, labelKey, valueKey, title, color = '#3b82f6' }) => {
    if (!data || data.length === 0) return null;
    const max = Math.max(...data.map(d => Number(d[valueKey]) || 0), 1);
    
    return (
      <div className="chart-card" style={{ marginTop: '24px', padding: '20px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0; border-color: var(--border-primary)' }}>
        <h4 style={{ margin: '0 0 20px 0', fontSize: '1rem', color: '#1e293b' }}>{title}</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {data.map((item, i) => {
            const val = Number(item[valueKey]) || 0;
            const percentage = (val / max) * 100;
            return (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>
                  <span>{item[labelKey]}</span>
                  <span>{val}</span>
                </div>
                <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                  <div 
                    style={{ 
                      height: '100%', 
                      width: `${percentage}%`, 
                      background: color || (i % 2 === 0 ? '#3b82f6' : '#8b5cf6'),
                      borderRadius: '4px',
                      transition: 'width 0.5s ease'
                    }} 
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const totalHazards = Number(reportData?.categoryDistribution?.reduce((acc, curr) => acc + Number(curr.count || 0), 0) || 0);
  const highSeverityCount = reportData?.severityDistribution?.filter(s => 
    s.severityLevelName?.toLowerCase().includes('high') || s.severityLevelName?.toLowerCase().includes('critical') || 
    s.severityLevelName?.toLowerCase().includes('med')
  ).reduce((acc, curr) => acc + Number(curr.count || 0), 0) || 0;
  const pendingActions = Number(reportData?.immediateActionStats?.pending || 0);
  const completionRate = totalHazards > 0 
    ? (((totalHazards - pendingActions) / totalHazards) * 100).toFixed(1) 
    : "0.0";

  const STAT_CARDS = [
    { label: "Total Hazards", icon: HiOutlineExclamationTriangle, color: "#3b82f6", borderColor: "#3b82f6", count: totalHazards, sub: "In selected period" },
    { label: "High/Critical", icon: HiOutlineExclamationTriangle, color: "#ef4444", borderColor: "#ef4444", count: highSeverityCount, sub: "Require attention" },
    { label: "Pending Actions", icon: HiOutlineClock, color: "#f59e0b", borderColor: "#f59e0b", count: pendingActions, sub: "Needs immediate action" },
    { label: "Completion Rate", icon: HiOutlineCheckCircle, color: SEMANTIC_SUCCESS_MID, borderColor: SEMANTIC_SUCCESS_MID, count: `${completionRate}%`, sub: "Actions resolved" },
  ];

  if (!canView) {
    return (
      <div className="pagecontainer report-page">
        <div className="error-message">
          You do not have permission to view hazard reports.
        </div>
      </div>
    );
  }

  return (
    <div className="pagecontainer report-page">
      <div className="page-header">
        <div className="oee-report-header-left">
          <button className="oee-report-back-btn" onClick={() => navigate("/home/reports")} title="Back to Reports">
            <HiOutlineArrowLeft size={18} />
          </button>
          <div>
            <h1 className="oee-report-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {tabs.find((t) => t.id === activeTab)?.icon && React.createElement(tabs.find((t) => t.id === activeTab).icon)}
              {activeTab === 'category' ? "Hazard Category Report" : 
               activeTab === 'severity' ? "Hazard Severity Report" :
               activeTab === 'action' ? "Hazard Action Report" :
               activeTab === 'department' ? "Hazard Department Report" : 
               activeTab === 'reporter' ? "Hazard Reporter Report" : "Hazard Reports"}
            </h1>
            <p className="oee-report-subtitle">
              {activeTab === 'category' ? "Monitor and analyze the distribution of hazards across different safety categories" :
               activeTab === 'severity' ? "Analyze the severity levels of reported hazards to prioritize safety responses" :
               activeTab === 'action' ? "Track the resolution status and immediate actions taken for hazards" :
               activeTab === 'department' ? "Identify which departments are reporting the most hazards" :
               "Identify the most active personnel submitting hazard reports"}
            </p>
          </div>
        </div>
        <div className="header-actions">
          <button className="oee-filter-toggle-btn" onClick={() => setShowFilters((p) => !p)}>
            <HiOutlineCalendar size={16} /> Filters {showFilters ? <HiOutlineChevronUp size={13} /> : <HiOutlineChevronDown size={13} />}
          </button>
          <button className="btn-primary btn" onClick={exportToExcel} disabled={loading || !reportData}>
            <HiOutlineDocumentArrowDown /> Export
          </button>
        </div>
      </div>

      {showFilters && (
        <>
          <div className="reportbox searchfilterbox">
            <div className="report-filters">
              <div className="filter-group"><label>Start Date</label><input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} className={`filter-input ${error && !filters.startDate ? "input-error" : ""}`} /></div>
              <div className="filter-group"><label>End Date</label><input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} className={`filter-input ${error && !filters.endDate ? "input-error" : ""}`} /></div>
              <div className="filter-group">
                <label>Plants</label>
                <MultiSelectDropdown
                  options={plants}
                  selectedValues={filters.plantIds}
                  onChange={handlePlantChange}
                  placeholder="All Plants"
                  displayKey="plantName"
                  valueKey="plantId"
                />
              </div>
              <div className="filter-group">
                <label>Category</label>
                <select
                  name="categoryId"
                  value={filters.categoryId}
                  onChange={handleFilterChange}
                  className="filter-input"
                >
                  <option value="">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.categoryId || c.id} value={c.categoryId || c.id}>
                      {c.categoryName || c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Severity Level</label>
                <select
                  name="severityLevelId"
                  value={filters.severityLevelId}
                  onChange={handleFilterChange}
                  className="filter-input"
                >
                  <option value="">All Severities</option>
                  {severityLevels.map((s) => (
                    <option key={s.severityLevelId || s.id} value={s.severityLevelId || s.id}>
                      {s.severityLevelName || s.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="report-filter-actions">
              {(filters.plantId || filters.categoryId || filters.severityLevelId) && (
                <button 
                  className="oee-clear-filters-btn" 
                  onClick={() => {
                    setFilters({
                      startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
                      endDate: new Date().toISOString().split('T')[0],
                      plantId: '',
                      categoryId: '',
                      severityLevelId: ''
                    });
                    setReportData(null);
                  }}
                >
                  ✕ Clear Filters
                </button>
              )}
              <button type="button" className="btn-primary" onClick={handleGenerateReport} disabled={loading}>
                <HiOutlineCalendar /> Generate Report
              </button>
            </div>
          </div>
          {error && <p className="report-filter-error" style={{ color: '#ef4444', fontSize: '13px', marginTop: '12px', fontWeight: 600, textAlign: 'center' }}>{error}</p>}
        </>
      )}

      {reportData && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", marginBottom: "24px" }}>
          {STAT_CARDS.map((card, idx) => (
            <div key={idx} style={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0; border-color: var(--border-primary)", borderLeft: `4px solid ${card.borderColor}`, padding: "18px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}><div style={{ padding: "6px", background: `${card.borderColor}18`, borderRadius: "6px", color: card.borderColor }}><card.icon size={18} /></div><p style={{ margin: 0, fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>{card.label}</p></div>
              <h2 style={{ margin: "0 0 4px 0", fontSize: "26px", fontWeight: 700, color: card.color }}>{loading ? "—" : card.count}</h2><p style={{ margin: 0, fontSize: "11px", color: "#94a3b8" }}>{card.sub}</p>
            </div>
          ))}
        </div>
      )}

      <div className="reports-table">
        {loading ? <div className="loading-container">Loading report data...</div> : !reportData ? (
          <div className="report-placeholder"><HiOutlineClipboardDocumentList className="placeholder-icon" /><h3>Hazard Reports</h3><p>Select criteria and click "Generate Report" to view analytics</p></div>
        ) : (
          <div className="report-table-wrapper">
            {activeTab === "category" && <CategoryReportView data={reportData.categoryDistribution || []} />}
            {activeTab === "severity" && <SeverityReportView data={reportData.severityDistribution || []} />}
            {activeTab === "action" && <ActionReportView data={reportData.immediateActionStats || { taken: 0, pending: 0 }} />}
            {activeTab === "department" && <DepartmentReportView data={reportData.departmentDistribution || []} />}
            {activeTab === "reporter" && <ReporterReportView data={reportData.reporterDistribution || []} />}
          </div>
        )}
      </div>
    </div>
  );
};

const CategoryReportView = ({ data }) => {
  const total = data.reduce((acc, curr) => acc + Number(curr.count || 0), 0);
  return (
    <div className="report-view-container" style={{ padding: '24px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0; border-color: var(--border-primary)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <h3 style={{ margin: '0 0 20px 0', fontSize: '1.1rem', color: '#1e293b' }}>Hazard Distribution by Category</h3>
      <div style={{ overflowX: 'auto' }}>
        <table className="report-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc' }}>
              <th style={{ textAlign: 'left', padding: '14px 16px', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em', width: '25%' }}>Category</th>
              <th style={{ textAlign: 'center', padding: '14px 16px', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em', width: '10%' }}>Count</th>
              <th style={{ textAlign: 'left', padding: '14px 16px', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em', width: '65%' }}>Distribution</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, idx) => {
              const count = Number(item.count || 0);
              const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
              return (
                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.2s' }}>
                  <td style={{ padding: '14px 16px', fontWeight: 600, color: '#334155' }}>{item.categoryName}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 600, color: '#0f172a' }}>{count}</td>
                  <td style={{ padding: '14px 32px 14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                      <div style={{ flex: 1, height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${percentage}%`, height: '100%', background: '#3b82f6', borderRadius: '4px', transition: 'width 0.5s ease-out' }} />
                      </div>
                      <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#475569', minWidth: '45px', textAlign: 'right' }}>{percentage}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
            {data.length === 0 && <tr><td colSpan="3" style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>No records found</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SeverityReportView = ({ data }) => {
  const total = data.reduce((acc, curr) => acc + Number(curr.count || 0), 0);
  return (
    <div className="report-view-container" style={{ padding: '24px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0; border-color: var(--border-primary)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <h3 style={{ margin: '0 0 20px 0', fontSize: '1.1rem', color: '#1e293b' }}>Hazard Severity Breakdown</h3>
      <div style={{ overflowX: 'auto' }}>
        <table className="report-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc' }}>
              <th style={{ textAlign: 'left', padding: '14px 16px', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em', width: '25%' }}>Severity Level</th>
              <th style={{ textAlign: 'center', padding: '14px 16px', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em', width: '10%' }}>Count</th>
              <th style={{ textAlign: 'left', padding: '14px 16px', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em', width: '65%' }}>Distribution</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, idx) => {
              const name = item.severityLevelName?.toLowerCase() || "";
              const count = Number(item.count || 0);
              const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
              
              const isCritical = name.includes("critical");
              const isHigh = name.includes("high");
              const isMedium = name.includes("medium");
              
              const badgeBg = isCritical ? '#fee2e2' : isHigh ? '#ffedd5' : isMedium ? '#e0e7ff' : '#dcfce7';
              const badgeColor = isCritical ? '#b91c1c' : isHigh ? '#c2410c' : isMedium ? '#4338ca' : '#15803d';
              const progressBg = isCritical ? '#ef4444' : isHigh ? '#f97316' : isMedium ? '#6366f1' : '#22c55e';

              return (
                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.2s' }}>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ display: 'inline-block', padding: '6px 14px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', backgroundColor: badgeBg, color: badgeColor }}>
                      {item.severityLevelName}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 600, color: '#0f172a' }}>{count}</td>
                  <td style={{ padding: '14px 32px 14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                      <div style={{ flex: 1, height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${percentage}%`, height: '100%', background: progressBg, borderRadius: '4px', transition: 'width 0.5s ease-out' }} />
                      </div>
                      <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#475569', minWidth: '45px', textAlign: 'right' }}>{percentage}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
            {data.length === 0 && <tr><td colSpan="3" style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>No records found</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ActionReportView = ({ data }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "10px" }}>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
      <div style={{ padding: "32px", background: "#f0fdf4", borderRadius: "12px", border: "1px solid #dcfce7", textAlign: "center", boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
        <h4 style={{ margin: "0 0 12px 0", color: "#166534", fontSize: "1.1rem", textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions Taken</h4>
        <div style={{ fontSize: "3.5rem", fontWeight: 800, color: SEMANTIC_SUCCESS_MID, lineHeight: 1 }}>{data.taken}</div>
        <p style={{ margin: "16px 0 0 0", color: "#15803d", fontSize: "0.95rem", fontWeight: 500 }}>Hazards effectively addressed and secured</p>
      </div>
      <div style={{ padding: "32px", background: "#fef2f2", borderRadius: "12px", border: "1px solid #fee2e2", textAlign: "center", boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
        <h4 style={{ margin: "0 0 12px 0", color: "#991b1b", fontSize: "1.1rem", textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pending Actions</h4>
        <div style={{ fontSize: "3.5rem", fontWeight: 800, color: "#ef4444", lineHeight: 1 }}>{data.pending}</div>
        <p style={{ margin: "16px 0 0 0", color: "#b91c1c", fontSize: "0.95rem", fontWeight: 500 }}>Requires your immediate attention</p>
      </div>
    </div>
    <div className="report-summary" style={{ padding: '24px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0; border-color: var(--border-primary)', display: 'flex', alignItems: 'center', gap: '16px' }}>
      <div style={{ width: '40px', height: '40px', background: '#e2e8f0', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg fill="none" viewBox="0 0 24 24" stroke="#64748b" width="20" height="20"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
      </div>
      <p style={{ margin: 0, fontWeight: 500, color: "#334155", lineHeight: '1.6', fontSize: '0.95rem' }}>
        <strong>Action Highlights:</strong> Out of {Number(data.taken) + Number(data.pending)} total recorded hazards, <span style={{ color: SEMANTIC_SUCCESS_MID, fontWeight: 700 }}>{data.taken}</span> were resolved and closed out, while <span style={{ color: '#ef4444', fontWeight: 700 }}>{data.pending}</span> are still open and pending root cause analysis.
      </p>
    </div>
  </div>
);

const DepartmentReportView = ({ data }) => {
  const total = data.reduce((acc, curr) => acc + Number(curr.count || 0), 0);
  return (
    <div className="report-view-container" style={{ padding: '24px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0; border-color: var(--border-primary)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <h3 style={{ margin: '0 0 20px 0', fontSize: '1.1rem', color: '#1e293b' }}>Hazards by Department</h3>
      <div style={{ overflowX: 'auto' }}>
        <table className="report-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc' }}>
              <th style={{ textAlign: 'left', padding: '14px 16px', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em', width: '25%' }}>Department</th>
              <th style={{ textAlign: 'center', padding: '14px 16px', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em', width: '10%' }}>Count</th>
              <th style={{ textAlign: 'left', padding: '14px 16px', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em', width: '65%' }}>Distribution</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, idx) => {
              const count = Number(item.count || 0);
              const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
              return (
                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.2s' }}>
                  <td style={{ padding: '14px 16px', fontWeight: 600, color: '#334155' }}>{item.departmentName}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 600, color: '#0f172a' }}>{count}</td>
                  <td style={{ padding: '14px 32px 14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                      <div style={{ flex: 1, height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${percentage}%`, height: '100%', background: '#8b5cf6', borderRadius: '4px', transition: 'width 0.5s ease-out' }} />
                      </div>
                      <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#475569', minWidth: '45px', textAlign: 'right' }}>{percentage}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
            {data.length === 0 && <tr><td colSpan="3" style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>No records found</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ReporterReportView = ({ data }) => {
  const total = data.reduce((acc, curr) => acc + Number(curr.count || 0), 0);
  return (
    <div className="report-view-container" style={{ padding: '24px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0; border-color: var(--border-primary)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <h3 style={{ margin: '0 0 20px 0', fontSize: '1.1rem', color: '#1e293b' }}>Top Hazard Reporters</h3>
      <div style={{ overflowX: 'auto' }}>
        <table className="report-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc' }}>
              <th style={{ textAlign: 'left', padding: '14px 16px', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em', width: '25%' }}>Reporter Name</th>
              <th style={{ textAlign: 'center', padding: '14px 16px', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em', width: '10%' }}>Frequency</th>
              <th style={{ textAlign: 'left', padding: '14px 16px', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em', width: '65%' }}>Activity Level</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, idx) => {
              const count = Number(item.count || 0);
              const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
              return (
                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.2s' }}>
                  <td style={{ padding: '14px 16px', fontWeight: 600, color: '#334155' }}>{item.reporterName}</td>
                  <td style={{ padding: '14px 16px', textAlign: 'center', fontWeight: 600, color: '#0f172a' }}>{count}</td>
                  <td style={{ padding: '14px 32px 14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                      <div style={{ flex: 1, height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${percentage}%`, height: '100%', background: SEMANTIC_SUCCESS_MID, borderRadius: '4px', transition: 'width 0.5s ease-out' }} />
                      </div>
                      <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#475569', minWidth: '45px', textAlign: 'right' }}>{percentage}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
            {data.length === 0 && <tr><td colSpan="3" style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>No records found</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HazardReports;
